import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, useTheme as useMuiSystemTheme } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { emotionCache } from '../utils/emotionConfig';
import { getCookie, setCookie } from './ThemeContext';
import { lightTheme, darkTheme } from './ThemeContext';
import {
  resolveMuiThemeContext,
  type ResolvedMuiThemeContext,
} from './resolveMuiThemeContext';

type MuiThemeContextType = ResolvedMuiThemeContext;

const MuiThemeContext = createContext<MuiThemeContextType | undefined>(undefined);

interface MuiThemeProviderProps {
  children: ReactNode;
}

export const MuiThemeContextProvider: React.FC<MuiThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const savedTheme = getCookie('theme-mode') as 'light' | 'dark' | null;
    if (savedTheme === 'light' || savedTheme === 'dark') {
      setMode(savedTheme);
    } else {
      const prefersDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, []);

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    setCookie('theme-mode', newMode);
  };

  const theme = mode === 'light' ? lightTheme : darkTheme;
  const value: MuiThemeContextType = {
    theme,
    mode,
    toggleTheme,
  };

  return (
    <MuiThemeContext.Provider value={value}>
      <CacheProvider value={emotionCache}>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </CacheProvider>
    </MuiThemeContext.Provider>
  );
};

export const useMuiTheme = (): MuiThemeContextType => {
  const context = useContext(MuiThemeContext);
  const muiTheme = useMuiSystemTheme();
  // Prod: fall back when a lazy chunk loaded a second MuiThemeContext copy (ST-2026-156).
  // Dev: still throw so missing providers are caught locally.
  return resolveMuiThemeContext(context, muiTheme, { strict: import.meta.env.DEV });
};
